import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { announceCartLinesUpdate, settleWithFetchedCart, announceCartError } from '@theme/standard-cart-events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement | undefined} variantId - The form input for submitting the variant ID (optional).
 * @property {AddToCartComponent} addToCartButton - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {number | undefined} */
  #timeout;

  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();

    const target = this.closest('.shopify-section, dialog');
    if (target) {
      this.#eventTarget = target;
      target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Cleanup timeout
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = undefined;
    }

    if (this.#eventTarget) {
      this.#eventTarget.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
      this.#eventTarget = null;
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = this.querySelector('form');
    if (!form?.checkValidity()) {
      const firstInvalidField = form.querySelector(':invalid');
      if (firstInvalidField) {
        firstInvalidField.focus();
        firstInvalidField.reportValidity();
      }
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.addToCartButton?.getAttribute('disabled') === 'true') return;

    const variantPicker = this.#eventTarget?.querySelector('variant-picker');
    if (variantPicker?.isUpdating) {
      this.addToCartButton.classList.add('btn--loading');
      await variantPicker.updateComplete;

      if (this.addToCartButton?.getAttribute('disabled') === 'true') {
        this.addToCartButton.classList.remove('btn--loading');
        return;
      }
    }

    this.#submitToCart();
  }

  #submitToCart() {
    const { addToCartError, addToCartTextError } = this.refs;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    this.addToCartButton.classList.add('btn--loading');

    const formData = new FormData(form);

    // Collect all cart section IDs to update after adding to cart
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    formData.append('sections', cartItemComponentsSectionIds.join(','));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const announcement = announceCartLinesUpdate(this, {
      action: 'add',
      context: 'product',
      lines: [
        {
          merchandiseId: String(formData.get('id') ?? ''),
          quantity: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1,
        },
      ],
    });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const errorMessage = response.description || response.message;

          announcement.reject(new Error(errorMessage));
          announceCartError(this, {
            error: errorMessage,
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          });
          window.dispatchEvent(new CartErrorEvent(this.id, errorMessage));

          this.addToCartButton.classList.remove('btn--loading');
          if (!addToCartError || !addToCartTextError) return;

          addToCartError.classList.remove('hidden');

          addToCartTextError.textContent = errorMessage;

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(errorMessage);

          this.addToCartButton.classList.add('has-error');

          this.#timeout = setTimeout(() => {
            if (!addToCartError) return;
            addToCartError.classList.add('hidden');
            this.addToCartButton.classList.remove('has-error');
            // Clear the announcement
            this.#clearLiveRegionText();
          }, 5000);

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          this.addToCartButton.classList.remove('btn--loading');
          document.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          settleWithFetchedCart(announcement, {
            source: 'product-form-component',
            sourceId: this.id,
            productId: this.dataset.productId,
            sections: response.sections,
          });
        }
      })
      .catch((error) => {
        announcement.reject(error);
        announceCartError(this, { error: error?.message ?? String(error), code: 'SERVICE_UNAVAILABLE' });
        console.error(error);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    // Always update product ID if new product data exists
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    const { variantId } = this.refs;

     // Update the variant ID
     if (event.detail.resource?.id && variantId) {
      variantId.value = event.detail.resource.id;
    }

    const currentAddToCartButton = this.addToCartButton;
    const newAddToCartButton = event.detail.data.html?.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    if( ! currentAddToCartButton.hasAttribute('data-product-information') ) return;

    const backInStockButton = this.querySelector('[ref="backInStockButton"]');

    // Update the button state based on variant availability
    if (event.detail.resource == null || event.detail.resource.available == false) {
      this.addToCartButton.setAttribute('disabled', 'disabled');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', '');
      if (backInStockButton) {
        this.classList.add('show-back-in-stock');
      }
    } else {
      this.addToCartButton.removeAttribute('disabled');
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      if (backInStockButton) {
        this.classList.remove('show-back-in-stock');
      }
    }

    // Update the add to cart button text and icon if new button exists
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }
   
  };


  get addToCartButton() {
    return this.refs.addToCartButton;
  }

  get mediaGallery() {
    return this.querySelector('media-gallery');
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}